<script setup lang="ts">
/**
 * The bell's 待处理 line: how many things need an operator — the
 * dashboard's 待处理 list, counted — and a way to it. Not messages, so
 * not in the 消息中心: they go when dealt with, not when read. This
 * console's own; the bell (NavNotifications) is shared.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { attentionItems, loadDashSummary, type DashSummary } from '@/utils/dashboard'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ count: [n: number]; go: [route: string] }>()
const { t } = useI18n()

const sum = ref<DashSummary | null>(null)
const items = computed(() => attentionItems(sum.value))
const total = computed(() => items.value.reduce((n, a) => n + Number(a.n), 0))
watch(() => items.value.length, (n) => emit('count', n), { immediate: true })

async function load() {
  try {
    sum.value = await loadDashSummary()
  } catch {
    // A bell that cannot ring is not worth an error toast on every page.
  }
}
let timer: number | undefined
onMounted(() => {
  void load()
  timer = window.setInterval(load, 120_000)
})
onUnmounted(() => window.clearInterval(timer))
watch(() => props.open, (v) => v && void load())
</script>

<template>
  <button v-if="items.length" type="button" class="attention" @click="emit('go', 'dashboard')">
    <icon-exclamation-circle-fill class="attention-icon" />
    <span class="attention-text">{{ t('inbox.attention', { n: total }) }}</span>
    <span class="attention-tags">
      <a-tag v-for="a in items.slice(0, 3)" :key="a.key" :color="a.color" size="small">{{ t(`dashboard.tag_${a.tag}`) }}</a-tag>
    </span>
    <icon-right class="attention-go" />
  </button>
</template>

<style scoped src="./attention.css"></style>
